import {
  loadImageToCanvas,
  setBase64Gif,
} from "../../sidebar/campaigns/custom-images.js";

import {
  initTemplates,
  showAttachmentsModal,
} from "../../sidebar/campaigns/attachments.js";
import {
  messageAlert,
  showToast,
  uuidV4,
  wrappedGet,
  wrappedPost,
} from "../utils.js";
import {
  getAiVariables,
  showAiVariablesModal,
} from "../../sidebar/campaigns/edit-ai-variables.js";
import { onClickRefreshAiVariables } from "../../sidebar/outreach/outreach.js";

const DATE_VARIABLE_EXPLAINERS = {
  emailAddress:
    '<span style="color: #1da2d8">emailAddress</span> is replaced by the primary email address of the recipient.',
  tomorrow:
    '<span style="color: #1da2d8">tomorrow</span> is replaced by "Tomorrow", unless the message is sent on a Friday/Saturday, in which case it is replaced by "Monday".',
  previousStepDay:
    '<p><span style="color: #1da2d8">previousStepDay</span> refers to the date the previous email or manual task was completed.<br>It can take values such as:</p><p>"Yesterday"</p><p>"on Saturday"</p><p>"last Wednesday"</p><p>"a couple of weeks ago"</p><p>"in January"</p>',
};

const DATE_VARIABLES = {
  emailAddress: "Email Address",
  timeOfDay: "Time of Day",
  dayOfWeek: "Day of the Week",
  tomorrow: "Tomorrow / Monday",
  twoWorkingDays: "Two Working Days",
  previousStepDay: "Previous Step Day",
};

export const addCustomVariablesButton = ({
  editor,
  getUser,
  customVariables,
}) =>
  editor.ui.registry.addMenuButton("customVariablesBtn", {
    icon: "",
    tooltip: "Variables",
    fetch: (callback) => {
      const user = getUser();
      const ret = [];

      if (user.aiVariables && customVariables.aiVariables) {
        ret.push([
          {
            type: "nestedmenuitem",
            text: "AI Variables",
            getSubmenuItems: () => {
              // const aiVariables = getAiVariables();
              // Populate the drop-down
              const aiVariableSubMenuItems = [];

              // To edit listed AI variables.
              aiVariableSubMenuItems.push({
                type: "menuitem",
                text: "Edit AI Variables",
                onAction: showAiVariablesModal,
              });

              aiVariableSubMenuItems.push({ type: "separator" });

              getAiVariables().forEach((aiVariable) => {
                aiVariableSubMenuItems.push({
                  type: "menuitem",
                  text: aiVariable.name,
                  onAction: () =>
                    tinymce.activeEditor.insertContent(
                      `<span contentEditable="false" class="ai-var" title="${aiVariable.prompt}" data-id="${aiVariable.aiVariableId}" style="color: #1da2d8">{(${aiVariable.embeddedName})}</span>`,
                    ),
                });
              });

              return aiVariableSubMenuItems;
            },
          },
        ]);
      }

      if (customVariables.customVariables) {
        ret.push(
          user.customVariables.map((customVar) => ({
            type: "menuitem",
            text: customVar.title,
            onAction: () =>
              tinymce.activeEditor.insertContent(
                `<span contentEditable="false" class="custom-var" style="color: #1da2d8">{{${customVar.key}}}</span>`,
              ),
          })),
        );
      }

      if (customVariables.dateVariables) {
        ret.push(
          Object.entries(DATE_VARIABLES).map(([name, title]) => ({
            type: "menuitem",
            text: title,
            onAction: () => {
              tinymce.activeEditor.insertContent(
                `<span contentEditable="false" class="custom-var" style="color: #1da2d8">{[${name}]}</span>`,
              );
              if (DATE_VARIABLE_EXPLAINERS[name]) {
                showToast({
                  title: DATE_VARIABLE_EXPLAINERS[name],
                  timeout: 45000,
                });
              }
            },
          })),
        );
      }

      if (customVariables.spintax) {
        ret.push([
          {
            type: "menuitem",
            text: "Spintax",
            onAction: () => {
              const spintaxId = `spintax-${uuidV4()}`;
              tinymce.activeEditor.insertContent(
                `<span contentEditable="false" id="${spintaxId}" class="spintax-var" title="Spintax Variable" style="color: #1da2d8" data-choices='[]'>{{ Spintax }}</span>`,
              );

              tinymce.activeEditor.fire("spintax-added", {
                target: tinymce.activeEditor.dom.doc.querySelector(
                  `#${spintaxId}`,
                ),
              });
            },
          },
        ]);
      }

      if (customVariables.senderCustomVariables) {
        ret.push(
          user.senderCustomVariables.map((customVar) => ({
            type: "menuitem",
            text: customVar.title,
            onAction: () =>
              tinymce.activeEditor.insertContent(
                `<span contentEditable="false" class="custom-var" style="color: #1da2d8">{[${customVar.key}]}</span>`,
              ),
          })),
        );
      }

      if (
        ["sourcewhale", "tironem"].includes(user.teamId) &&
        customVariables.commentVariables
      ) {
        ret.push(
          user.commentCustomVariables.map((customVar) => ({
            type: "menuitem",
            text: customVar.title,
            onAction: () =>
              tinymce.activeEditor.insertContent(
                `<span contentEditable="false" class="custom-var" style="color: #1da2d8">{{${customVar.key}}}</span>`,
              ),
          })),
        );
      }

      callback(
        ret
          .filter((item) => item.length)
          .map((x) => [...x, { type: "separator" }])
          .flat(),
      );
    },
  });

export const templateDerivations = {};

export const addTemplatesButton = ({ editor, templateId, getAttachmentIds }) =>
  editor.ui.registry.addMenuButton("templatesBtn", {
    icon: "",
    tooltip: "Insert template",
    fetch: (callback) => {
      wrappedGet({
        url: "/api/templates/get_all",
        success: (res) => {
          let items = [];
          items.push({
            type: "menuitem",
            text: "Save content as template",
            icon: "save",
            onAction: () => {
              editor.windowManager.open({
                title: "Save Template",
                body: {
                  type: "panel",
                  items: [
                    {
                      type: "input",
                      name: "templateNameInput",
                      inputMode: "text",
                      placeholder: "Template Name...",
                    },
                  ],
                },
                buttons: [
                  { type: "cancel", text: "Cancel" },
                  { type: "submit", text: "Save" },
                ],
                onSubmit: (api) => {
                  const title = api.getData().templateNameInput;
                  const content = editor.getContent();
                  if (!title.length) return;
                  if (!content.length) {
                    showToast({
                      title: "Content is empty",
                      subtitle: "Please add content to the template",
                      type: "warning",
                    });
                    return;
                  }
                  api.close();
                  const attachmentIds = getAttachmentIds();
                  wrappedPost({
                    url: "/api/templates/save",
                    data: JSON.stringify({
                      title,
                      content,
                      attachmentIds,
                    }),
                    success: (saveRes) => {
                      showToast({
                        title: "Template saved",
                        type: "success",
                      });
                      $(".tox-toolbar__overflow").hide();

                      templateDerivations[templateId] = saveRes.teamTemplateId;
                    },
                  });
                },
              });
            },
          });
          items.push({ type: "separator" });
          items.push({
            type: "menuitem",
            text: "Manage templates",
            icon: "new-tab",
            onAction: () => window.open("/templates", "_blank"),
          });
          items.push({ type: "separator" });
          items = items.concat(
            res.map((item) => ({
              type: "menuitem",
              text:
                item.title.length > 60
                  ? `${item.title.substring(0, 57)}...`
                  : item.title,
              onAction: () => {
                editor.insertContent(item.content);

                if (window.location.pathname.includes("/outreach"))
                  initTemplates({
                    type: "outreach",
                    templateId,
                    attachmentIds: item.attachmentIds,
                    candidateTemplates: "useRef",
                  });
                else
                  initTemplates({
                    type: "single",
                    key: templateId,
                    attachmentIds: item.attachmentIds,
                  });

                $(".tox-toolbar__overflow").hide();
                templateDerivations[templateId] = item.teamTemplateId;
              },
            })),
          );
          callback(items);
        },
      });
    },
  });

export const addEmojiButton = ({ editor }) => {
  let button;
  const picker = new EmojiButton({
    position: "top",
  });
  picker.on("emoji", (emoji) => {
    tinymce.activeEditor.execCommand("mceInsertContent", false, ` ${emoji}`);
  });

  if (!document.head.querySelector("style#emojiPicker")) {
    const style = document.createElement("style");
    style.id = "emojiPicker";
    style.textContent = `.emoji-picker {z-index: 2000;}`;
    document.head.appendChild(style);
  }

  editor.ui.registry.addButton("emojiBtn", {
    icon: "",
    tooltip: "Emoticons",
    onSetup: () => {
      button = editor.editorContainer.querySelector(
        `[aria-label^="Emoticons"]`,
      );
    },
    onAction: () => {
      if (picker.pickerVisible) {
        picker.hidePicker();
        return;
      }
      picker.showPicker(button);
    },
  });
};

export const addDirectionalityButton = ({ editor }) => {
  let ltr = true;
  editor.ui.registry.addButton("directionalityBtn", {
    icon: "",
    tooltip: "Toggle text direction",
    onAction: () => {
      if (ltr) editor.execCommand("mceDirectionRTL");
      else editor.execCommand("mceDirectionLTR");
      ltr = !ltr;
    },
  });
};

export const addCodeButton = ({ editor }) => {
  editor.ui.registry.addIcon(
    "codeIcon",
    '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-filetype-html" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M14 4.5V11h-1V4.5h-2A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v9H2V2a2 2 0 0 1 2-2h5.5zm-9.736 7.35v3.999h-.791v-1.714H1.79v1.714H1V11.85h.791v1.626h1.682V11.85h.79Zm2.251.662v3.337h-.794v-3.337H4.588v-.662h3.064v.662zm2.176 3.337v-2.66h.038l.952 2.159h.516l.946-2.16h.038v2.661h.715V11.85h-.8l-1.14 2.596H9.93L8.79 11.85h-.805v3.999zm4.71-.674h1.696v.674H12.61V11.85h.79v3.325Z"/></svg>',
  );
  return editor.ui.registry.addButton("codeBtn", {
    icon: "codeIcon",
    tooltip: "Insert Raw HTML",
    onAction: () => editor.execCommand("mceCodeEditor"),
  });
};

export const addAiVariablesRefreshButton = ({
  editor,
  aiVariablesTemplateId,
}) => {
  editor.ui.registry.addIcon(
    "aiVariableRefreshIcon",
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" focusable="false" width="16" height="16"><!--! Font Awesome Pro 6.4.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2023 Fonticons, Inc. --><path d="M234.7 42.7L197 56.8c-3 1.1-5 4-5 7.2s2 6.1 5 7.2l37.7 14.1L248.8 123c1.1 3 4 5 7.2 5s6.1-2 7.2-5l14.1-37.7L315 71.2c3-1.1 5-4 5-7.2s-2-6.1-5-7.2L277.3 42.7 263.2 5c-1.1-3-4-5-7.2-5s-6.1 2-7.2 5L234.7 42.7zM46.1 395.4c-18.7 18.7-18.7 49.1 0 67.9l34.6 34.6c18.7 18.7 49.1 18.7 67.9 0L529.9 116.5c18.7-18.7 18.7-49.1 0-67.9L495.3 14.1c-18.7-18.7-49.1-18.7-67.9 0L46.1 395.4zM484.6 82.6l-105 105-23.3-23.3 105-105 23.3 23.3zM7.5 117.2C3 118.9 0 123.2 0 128s3 9.1 7.5 10.8L64 160l21.2 56.5c1.7 4.5 6 7.5 10.8 7.5s9.1-3 10.8-7.5L128 160l56.5-21.2c4.5-1.7 7.5-6 7.5-10.8s-3-9.1-7.5-10.8L128 96 106.8 39.5C105.1 35 100.8 32 96 32s-9.1 3-10.8 7.5L64 96 7.5 117.2zm352 256c-4.5 1.7-7.5 6-7.5 10.8s3 9.1 7.5 10.8L416 416l21.2 56.5c1.7 4.5 6 7.5 10.8 7.5s9.1-3 10.8-7.5L480 416l56.5-21.2c4.5-1.7 7.5-6 7.5-10.8s-3-9.1-7.5-10.8L480 352l-21.2-56.5c-1.7-4.5-6-7.5-10.8-7.5s-9.1 3-10.8 7.5L416 352l-56.5 21.2z"/></svg>',
  );
  editor.ui.registry.addButton("aiVariableRefreshBtn", {
    icon: "aiVariableRefreshIcon",
    tooltip: "Refresh AI Variables",
    onAction: () => onClickRefreshAiVariables(aiVariablesTemplateId),
  });
};

export const addCustomButton = ({ editor }) =>
  editor.ui.registry.addButton("customButton", {
    text: "Custom Button",
    tooltip: "Insert Custom Button",
    onAction: () => {
      editor.windowManager.open({
        title: "Insert Custom Button",
        body: {
          type: "panel",
          classes: "custom-button-container",
          items: [
            {
              type: "input",
              name: "text",
              inputMode: "text",
              placeholder: "Text",
            },
            {
              type: "input",
              name: "url",
              inputMode: "text",
              placeholder: "Link",
            },
          ],
        },
        buttons: [{ type: "submit", text: "Insert" }],
        onSubmit: (api) => {
          const data = api.getData();
          const buttonStyling =
            "align-items:center;background-color:transparent;border-left:8px solid white;border-left:10px solid white;border-right:10px solid white;border-bottom:12px solid white;border-top:12px solid white;outline:2px solid #1da2d8;border-radius:5px;color:#1da2d8;display:inline-block;font-family:Helvetica Neue,sans-serif;font-size:1rem;font-weight:400;line-height:1;margin:2px;text-decoration:none;user-select:none;white-space:nowrap";
          const customButtonHtml = `<a href="https://${data.url
            .replace("http://", "")
            .replace(
              "https://",
              "",
            )}" target="_blank" class="sw-custom-btn" style="${buttonStyling}">${
            data.text
          }</a>`;
          editor.insertContent(customButtonHtml);
          editor.windowManager.close();
        },
      });
    },
  });

export const addImageGifButton = ({ editor }) =>
  editor.ui.registry.addButton("imageGifBtn", {
    icon: "",
    tooltip: "Insert/edit image/GIF",
    onAction: () => {
      const node = editor.selection.getNode();
      if ($(node).hasClass("sw-custom-image")) {
        $("#LoadingDiv").removeClass("invisible");
        wrappedPost({
          url: "/api/campaigns/get_custom_image",
          data: JSON.stringify({ src: $(node).attr("src") }),
          success: (res) => {
            const image = new Image();
            if ($(node).hasClass("sw-custom-image-gif")) {
              image.src = `data:image/gif;base64,${res.customImageTemplate.backgroundImage.src}`;
              setBase64Gif(res.customImageTemplate.backgroundImage.src);
              loadImageToCanvas({
                file: "gif",
                image,
                existingImage: {
                  ...res.customImageTemplate,
                  backgroundImage: {
                    ...res.customImageTemplate.backgroundImage,
                    src: image.src,
                  },
                },
              });
            } else {
              image.src = res.customImageTemplate.backgroundImage.src;
              loadImageToCanvas({
                file: "static",
                image,
                existingImage: res.customImageTemplate,
              });
            }
            $("#LoadingDiv").addClass("invisible");
          },
          error: () => $("#LoadingDiv").addClass("invisible"),
        });
      } else $("#insertImageGifModal").modal("show");
      tinymce.get(editor.settings.id).focus();
    },
  });

export const addAttachmentsButton = ({
  editor,
  getUser,
  currentAttachmentIdsStore,
}) =>
  editor.ui.registry.addButton("attachmentsBtn", {
    icon: "",
    tooltip: "Insert attachment",
    onAction: () => {
      const user = getUser();
      if (!user.hasTeamDomain) {
        messageAlert(
          'A custom domain must be set up to send attachments.<br>Please follow the instructions <a href="https://help.sourcewhale.app/en/articles/7046455-how-to-create-a-custom-domain" target="blank_">here</a>.',
        );
        return;
      }
      tinymce.get(editor.settings.id).focus();
      $("#hyperlinkText").val("");
      showAttachmentsModal(editor, currentAttachmentIdsStore);
    },
  });

export const addVideoButton = ({ editor }) =>
  editor.ui.registry.addButton("videoBtn", {
    icon: "",
    tooltip: "Insert video",
    onAction: () => {
      editor.windowManager.open({
        title: "Insert video",
        body: {
          type: "panel",
          items: [
            {
              type: "htmlpanel",
              html: "<p>Enter a URL from Dubb, Hinterview, Hippo Video, Loom, Odro, Sendspark, Vidyard, Vinneo, Wistia, or YouTube.",
            },
            {
              type: "input",
              name: "videoSourceInput",
              inputMode: "text",
              placeholder: "Source...",
            },
          ],
        },
        buttons: [{ type: "submit", text: "Insert" }],
        onSubmit: (api) => {
          $.post({
            url: "/api/campaigns/embed_media",
            data: JSON.stringify({ url: api.getData().videoSourceInput }),
            success: (res) => {
              editor.insertContent(res);
              editor.windowManager.close();
            },
            error: (err) => {
              if (err.status)
                showToast({
                  title:
                    "This URL cannot be embedded. Please use a URL from a supported video site.",
                  type: "danger",
                });
            },
          });
        },
      });
    },
  });

export const addSourceWhaleButtonsByToolbar = (toolbar, arg) => {
  const funcMap = {
    aiVariableRefreshBtn: addAiVariablesRefreshButton,
    attachmentsBtn: addAttachmentsButton,
    codeBtn: addCodeButton,
    customButton: addCustomButton,
    customVariablesBtn: addCustomVariablesButton,
    directionalityBtn: addDirectionalityButton,
    emojiBtn: addEmojiButton,
    imageGifBtn: addImageGifButton,
    templatesBtn: addTemplatesButton,
    videoBtn: addVideoButton,
  };
  if (typeof toolbar === "string") {
    toolbar
      .split(" ")
      .filter((word) => funcMap[word])
      .forEach((btn) => {
        funcMap[btn](arg);
      });
  } else if (typeof toolbar === "object" && toolbar instanceof Array) {
    toolbar.forEach((tb) => addSourceWhaleButtonsByToolbar(tb, arg));
  }
};
